/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dropdown, Modal } from "semantic-ui-react";
import { TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { resourceAdded, selectAllResources, selectResourceById, selectResourceCollectionStatus } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import { selectComparativeEvidenceMap } from "../slices/comparativeEvidenceMap/comparativeEvidenceMapSlice";
import { globalLoadingOff, globalLoadingOn } from "../slices/globalLoading/globalLoadingSlice";
import getSearchResults from "../functions/getSearchResultsAPI";
import submitFhirJson from "../functions/submitFhirJsonAPI";
import getFhirJson from "../functions/getFhirJsonAPI";
import jsonEquals from "../functions/jsonEquals";
import OutcomeImportanceEdit from "../components/OutcomeImportanceEdit";
import DocumentReferenceResourceEdit from "../components/DocumentReferenceEdit";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import RecommendationJustificationEdit from "../components/RecommendationJustificationEdit";
import EvidenceResourceEdit from "../resourceComponents/editReferencedResourceComponents/EvidenceResourceEdit";
import ResearchStudyResourceEdit from "../resourceComponents/editReferencedResourceComponents/ResearchStudyResourceEdit";
import GroupResourceEdit from "../resourceComponents/editReferencedResourceComponents/GroupResourceEdit";
import EvidenceVariableResourceEdit from "../resourceComponents/editReferencedResourceComponents/EvidenceVariableResourceEdit";
import addProfileValuesToNewResource from "../resourceComponents/profileFunctions/addProfileValuesToNewResource";
import createJsonForNewResource from "../resourceComponents/profileFunctions/createJsonForNewResource";
import NewTabLink from "../components/NewTabLink";
import CodeEntry from "./CodeEntry";
import StringEntry from "./StringEntry";
import CodeArrayEntry from "./CodeArrayEntry";
import IdentifierEntry, { displayStringFromFHIRIdentifier } from "./IdentifierEntry";
import { fevirPublicationStatusActive } from "../dictionaries/THOandFHIRValueSets";
import ProfilesByResourceType from "../dictionaries/ProfilesByResourceType";
import { allFevirResourceTypes, allFhirResourceTypes } from "../dictionaries/CodeSystemLookup";
import ContainedResourceDisplay from "../resourceComponents/resourceDisplayCommonComponents/ContainedResourceDisplay";

const SelectProfileForNewResource = ({ profileSelectionState, setProfileSelectionState }) => {
    if (!profileSelectionState) {
        console.log("MISSING profileSelectionState");
        return <></>
    } else if (profileSelectionState.profileSelected === "none") {
        return <></>
    } else if (!profileSelectionState.profileChoices) {
        console.log("MISSING profileSelectionState.profileChoices");
        return <></>
    } else if (profileSelectionState.profileChoices.length === 0) {
        console.log("EMPTY profileSelectionState.profileChoices");
        return <></>
    } else {
        return <div>
            {profileSelectionState.profileChoices.length > 0 &&
                <div style={{ marginLeft: "0px", marginBottom: "8px" }}>
                    <br />
                    <b>Select a Profile: </b>
                    &nbsp;
                    <ToggleButtonGroup
                        className="genericToggleButtonGroup buttonGroupWrap medium"
                        exclusive
                        value={profileSelectionState.profileSelected || ""}
                        aria-label='profile-selection'
                        name='profile-selection'
                    >
                        {profileSelectionState.profileChoices.map((profile, profileIndex) => {
                            return <ToggleButton key={profileIndex} className="genericToggleButton" color="primary"
                                aria-label={profile} value={profile}
                                onClick={(e) => {
                                    if (e.target.value) {
                                        let profileName = e.target.value;
                                        setProfileSelectionState(prevState => {
                                            if (profileName === prevState.profileSelected) {
                                                return {
                                                    ...prevState,
                                                    'profileSelected': undefined
                                                }
                                            } else {
                                                return {
                                                    ...prevState,
                                                    'profileSelected': profileName
                                                }
                                            }
                                        });
                                    }
                                }}>{profile}</ToggleButton>
                        })}
                    </ToggleButtonGroup>
                </div>}
        </div>
    }
};

const deliverSearchResults = async (apiToken, modalState, selectedResourceTypes, selectedProfiles, setModalState) => {
    if (modalState.modalOpen && modalState.searchQueryString &&
        modalState.searchQueryCompleted && !modalState.searchResultsDelivered) {
        let searchResults = await getSearchResults(modalState.searchQueryString,
            modalState.searchType,
            selectedResourceTypes,
            selectedProfiles,
            null,
            null,
            null,
            null,
            null,
            null,
            false,
            apiToken);
        setModalState(prevState => {
            return { ...prevState, "searchResults": searchResults, "searchResultsDelivered": true }
        })
    }
};

const SelectResourceFromSearchModal = ({ selectedResourceType, allowedResourceTypes,
    setSelectResourceFromSearchState, selectedProfile, allowedProfiles, setStartCollapsedState }) => {
    const dispatch = useDispatch();
    let startingSelectedResourceTypes = null;
    if (allowedResourceTypes) {
        startingSelectedResourceTypes = allowedResourceTypes;
    }
    if (selectedResourceType) {
        startingSelectedResourceTypes = [selectedResourceType];
    }
    let startingSelectedProfiles = null;
    if (allowedProfiles) {
        startingSelectedProfiles = allowedProfiles;
    }
    if (selectedProfile) {
        startingSelectedProfiles = [selectedProfile];
    }
    let startingModalState = { "modalOpen": true, "searchQueryString": "", "searchType": "Titles", "searchResults": "" };
    const [modalState, setModalState] = useState(startingModalState);
    const [searchResourceTypeLimiterState, setSearchResourceTypeLimiterState] = useState({
        "selectedResourceType": selectedResourceType,//TODO -- this key does not appear to ever get used
        "selectedResourceTypes": startingSelectedResourceTypes
    });
    const [searchProfileLimiterState, setSearchProfileLimiterState] = useState({
        "selectedProfile": selectedProfile,//TODO -- this key does not appear to ever get used
        "selectedProfiles": startingSelectedProfiles
    });
    const apiToken = useSelector(selectCurrentUserApiToken);

    useEffect((() => {
        const doTheSearch = async () => {
            dispatch(globalLoadingOn());
            await deliverSearchResults(apiToken, modalState, searchResourceTypeLimiterState.selectedResourceTypes,
                searchProfileLimiterState.selectedProfiles, setModalState); //TODO - NOW USES THE PLURAL - I don't know which one's the correct one selectedResourceTypes or selectedResourceType and the same for profile
            dispatch(globalLoadingOff());
        }
        doTheSearch();
    }), [modalState])

    const selectTheResource = (data) => {
        setModalState(prevState => { return { ...prevState, modalOpen: false }; });
        if (setStartCollapsedState) {
            setStartCollapsedState(true);
        }
        setSelectResourceFromSearchState(prevState => { return { ...prevState, "selectedResourceReference": data, modalOpen: false }; });
    }

    let modalContent = <div style={{
        paddingTop: "6px", paddingLeft: "20px", paddingRight: "20px",
        paddingBottom: "40px", width: "100%", height: "100%", overflow: "auto"
    }}>
        <Button style={{ padding: "6px", position: "absolute", right: "14px" }} className="formButton negative"
            content="✖"
            onClick={() => {
                setModalState(prevState => { return { ...prevState, modalOpen: false }; });
                setSelectResourceFromSearchState(prevState => { return { ...prevState, modalOpen: false }; });
            }} />
        {(!selectedResourceType) && <>
            <CodeArrayEntry elementName='selectedResourceTypes' fieldLabel='Limit by Resource Type'
                allowedValues={(allowedResourceTypes && allowedResourceTypes.length) ? allowedResourceTypes : allFevirResourceTypes} dataEntryStyle='dropdownsearch'
                startingValue={searchResourceTypeLimiterState.selectedResourceTypes || []}
                setResourceState={setSearchResourceTypeLimiterState} />
            <p style={{ paddingTop: "12px" }}>Limit to resource types of: {(searchResourceTypeLimiterState.selectedResourceTypes &&
                searchResourceTypeLimiterState.selectedResourceTypes.length > 0) ?
                searchResourceTypeLimiterState.selectedResourceTypes.join(', ') : "[No limits]"}</p>
        </>}
        {((selectedResourceType || searchResourceTypeLimiterState?.selectedResourceTypes?.length === 1) &&
            !selectedProfile
            && (allowedProfiles?.length ||
                (ProfilesByResourceType[selectedResourceType || searchResourceTypeLimiterState.selectedResourceTypes?.[0]]
                    && Object.keys(ProfilesByResourceType[selectedResourceType || searchResourceTypeLimiterState.selectedResourceTypes[0]]).length))
        ) && <>
                <br />
                <CodeArrayEntry elementName='selectedProfiles' fieldLabel='Limit by Profile'
                    allowedValues={(allowedProfiles?.length)
                        ?
                        allowedProfiles
                        :
                        ProfilesByResourceType[selectedResourceType ||
                            searchResourceTypeLimiterState.selectedResourceTypes[0]]
                            ?
                            Object.keys(ProfilesByResourceType[selectedResourceType ||
                                searchResourceTypeLimiterState.selectedResourceTypes[0]]
                            )
                            :
                            []}
                    dataEntryStyle='dropdownsearch'
                    startingValue={searchProfileLimiterState.selectedProfiles || []}
                    setResourceState={setSearchProfileLimiterState} />
                <p style={{ paddingTop: "12px" }}>
                    Limit to Profiles of: {(searchProfileLimiterState.selectedProfiles &&
                        searchProfileLimiterState.selectedProfiles.length > 0) ?
                        searchProfileLimiterState.selectedProfiles.join(', ') : "[No limits]"}</p>
                <br />
            </>}
        <h3>Enter search string <Dropdown
            name="searchType"
            placeholder="Search Type"
            closeOnChange selection selectOnBlur={false} autoComplete='off'
            style={{ marginLeft: "8px", fontSize: "14px", fontWeight: "bold", minWidth: "220px" }}
            options={[
                { key: "All Content", text: "All Content", value: "All Content" },
                //{ key: "Content & Element Names", text: "Content & Element Names", value: "Content & Element Names" },
                { key: "Titles", text: "Titles", value: "Titles" },
                /*{ key: "Titles & Descriptsions", text: "Titles & Descriptsions", value: "Titles & Descriptsions" }*/
            ]}
            value={modalState.searchType || ''}
            onChange={(e, data) => {
                setModalState(prevState => { return { ...prevState, "searchType": data.value, "searchQueryCompleted": (!!modalState.searchQueryString?.trim()), "searchResultsDelivered": false } });
            }}
        /></h3>
        <TextField style={{ width: "100%", marginTop: "16px" }}
            className="inputField" type='text' label={"Enter search string"} size="small" variant='outlined'
            value={modalState.searchQueryString || ""}
            onKeyUp={(event) => {
                if (event.key === 'Enter') {
                    setModalState(prevState => { return { ...prevState, "searchQueryCompleted": true, "searchResultsDelivered": false } });
                }
            }}
            onChange={(e) => {
                setModalState(prevState => { return { ...prevState, "searchQueryString": e.target.value } })
            }} />
        {modalState.searchResults && <>
            <h3 style={{ marginTop: "8px", paddingBottom: "4px" }}>Select a resource</h3>
            {modalState.searchResults.length > 0
                ?
                <>
                    {modalState.searchResults.map((result, resultIndex) => {
                        return <p key={resultIndex} style={{ marginBottom: "6px" }}>
                            <span style={{ cursor: "pointer" }}
                                onClick={() => { selectTheResource(result.data); }} ><b>SELECT</b></span>
                            &nbsp;&nbsp;
                            <span >{result.display}</span><span>{result.status === "draft" && <>&nbsp;&nbsp;(draft)</>}</span>
                            &nbsp;&nbsp;
                            <a href={"https://fevir.net" + result.onClick} target='_blank' rel='noopener noreferrer' >View on FEvIR</a>
                        </p>
                    })}
                </>
                :
                <>No results found.</>}
        </>
        }
        <br />
        <br />
    </div>;

    return <Modal
        style={{ padding: "0px", margin: "0px", maxHeight: "92vh", overflowY: "auto" }}
        dimmer={<Modal.Dimmer style={{ backgroundColor: "#00000077" }} />}
        open={modalState?.modalOpen}
        centered={false}
        content={modalContent}
    />;
}

const ReferencedResourceContentEntry = ({ referencedResourceJsonState,
    statisticType, statisticUnit, numberAnalyzed, setTriggerReloadState,
    setReferencedEvidenceRevisedState, setReferencedResourceState }) => {

    const targetResourceDetail = useSelector(state => selectResourceById(state, referencedResourceJsonState.id));
    const targetEditPermission = targetResourceDetail?.permissionData?.editpermission;

    let resourceType = referencedResourceJsonState.resourceType;
    if (!targetResourceDetail) {
        return <p>The resource you are seeking to edit is not loaded. Save your changes and refresh the page to try again.</p>
    }
    if (!targetEditPermission) {
        return <p>You do not have permission for EDITING RESOURCE CONTENT</p>;
    }

    if (resourceType === "Group" || resourceType === "EvidenceVariable" ||
        resourceType === "Evidence" || resourceType === "ResearchStudy" ||
        resourceType === "DocumentReference") {
        return <div>
            <h4>Change the content for this {resourceType} Resource.</h4>
            <div style={{ marginLeft: "24px", padding: "6px", outline: "2px solid #444444", borderRadius: "8px", backgroundColor: "#FFFFB0" }}>
                {resourceType === "Group" &&
                    <GroupResourceEdit foi={referencedResourceJsonState.id} />}
                {resourceType === "EvidenceVariable" &&
                    <EvidenceVariableResourceEdit foi={referencedResourceJsonState.id} />}
                {resourceType === "Evidence" &&
                    <EvidenceResourceEdit foi={referencedResourceJsonState.id}
                        statisticType={statisticType} statisticUnit={statisticUnit}
                        setReferencedResourceState={setReferencedResourceState}
                        numberAnalyzed={numberAnalyzed} setTriggerReloadState={setTriggerReloadState}
                        setReferencedEvidenceRevisedState={setReferencedEvidenceRevisedState} />}
                {resourceType === "ResearchStudy" &&
                    <ResearchStudyResourceEdit foi={referencedResourceJsonState.id} />}
                {resourceType === "DocumentReference" &&
                    <DocumentReferenceResourceEdit foi={referencedResourceJsonState.id} />}
            </div>
            <br />
        </div>
    } else if (resourceType === "ArtifactAssessment" &&
        referencedResourceJsonState?.meta?.profile?.[0] === "http://hl7.org/fhir/uv/ebm/StructureDefinition/recommendation-justification"
    ) {
        return <div>
            <h4>Change the content for this RecommendationJustification Resource.</h4>
            <div style={{ marginLeft: "24px", padding: "6px", outline: "2px solid #444444", borderRadius: "8px", backgroundColor: "#FFFFB0" }}>
                <RecommendationJustificationEdit foi={referencedResourceJsonState.id} />
            </div>
            <br />
        </div>
    } else if (resourceType === "ArtifactAssessment" &&
        referencedResourceJsonState?.meta?.profile?.[0] === "http://hl7.org/fhir/uv/ebm/StructureDefinition/outcome-importance"
    ) {
        return <div>
            <h4>Change the content for this OutcomeImportance Resource.</h4>
            <div style={{ marginLeft: "24px", padding: "6px", outline: "2px solid #444444", borderRadius: "8px", backgroundColor: "#FFFFB0" }}>
                <OutcomeImportanceEdit foi={referencedResourceJsonState.id} />
            </div>
            <br />
        </div>
    } else {
        return <p>THIS WILL BE FORM FOR EDITING RESOURCE CONTENT</p>
    }
};

const createNewReferencedResource = (resourceType, newResourceState, addElementValues) => {
    let newResource = createJsonForNewResource(newResourceState);

    newResource.resourceType = resourceType;
    if (addElementValues) {
        let partOfRelationshipReference;
        for (const key in addElementValues) {
            if (addElementValues[key] || addElementValues[key] === false || addElementValues[key] === 0) {
                if (key === "relatedArtifact" && resourceType === "Composition") {
                    newResource["relatesTo"] = addElementValues[key];
                } else if (key === "partOfRelationshipReference") {
                    partOfRelationshipReference = addElementValues[key];
                } else if (resourceType !== "List") {
                    newResource[key] = addElementValues[key];
                }
            }
        }
        if (partOfRelationshipReference) {
            if (resourceType === "Composition" || resourceType === "Citation" || resourceType === "Evidence" ||
                resourceType === "EvidenceVariable" || resourceType === "ArtifactAssessment" ||
                resourceType === "ResearchStudy"
            ) {
                let partOfRelatesTo = {
                    "type": {
                        "coding": [
                            {
                                "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                                "code": "part-of",
                                "display": "Part Of"
                            }
                        ]
                    },
                    "targetReference": partOfRelationshipReference
                };
                if (newResource.relatesTo && Array.isArray(newResource.relatesTo)) {
                    newResource.relatesTo.push(partOfRelatesTo);
                } else {
                    newResource.relatesTo = [partOfRelatesTo];
                }
            } else if (resourceType === "ActivityDefinition" || resourceType === "PlanDefinition" ||
                resourceType === "Library" || resourceType === "CodeSystem" ||
                resourceType === "ValueSet") {
                let partOfRelatedArtifact = {
                    "type": "part-of",
                    "resourceReference": partOfRelationshipReference
                };
                if (newResource.relatedArtifact && Array.isArray(newResource.relatedArtifact)) {
                    newResource.relatedArtifact.push(partOfRelatedArtifact);
                } else {
                    newResource.relatedArtifact = [partOfRelatedArtifact];
                }
            } else {
                let partOfRelatedArtifactExtension = {
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-relatedArtifact",
                    "valueRelatedArtifact": {
                        "type": "part-of",
                        "resourceReference": partOfRelationshipReference
                    }
                };
                if (newResource.extension && Array.isArray(newResource.extension)) {
                    newResource.extension.push(partOfRelatedArtifactExtension);
                } else {
                    newResource.extension = [partOfRelatedArtifactExtension];
                }
            }
        }
    }

    if (newResource.meta?.profile?.[0] === "http://hl7.org/fhir/uv/ebm/StructureDefinition/dichotomous-intended-outcome" &&
        !newResource.shortTitle) {
        newResource.shortTitle = newResource.title.replace("DichotomousIntendedOutcome: ", "");
    }
    if (newResource.extension) {
        let publicationExtensionFound = false;
        for (let extension of newResource.extension) {
            if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status") {
                extension.valueCodeableConcept = fevirPublicationStatusActive;
                publicationExtensionFound = true;
            }
        }
        if (!publicationExtensionFound) {
            newResource.extension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status",
                "valueCodeableConcept": fevirPublicationStatusActive
            });
        }
    } else {
        newResource.extension = [{
            "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status",
            "valueCodeableConcept": fevirPublicationStatusActive
        }]
    }
    if (resourceType === "Composition" || resourceType === "Citation" || resourceType === "Evidence" ||
        resourceType === "EvidenceVariable" || resourceType === "ResearchStudy" ||
        resourceType === "ActivityDefinition" || resourceType === "PlanDefinition" ||
        resourceType === "Library" || resourceType === "CodeSystem" ||
        resourceType === "ValueSet" || resourceType === "List"
    ) {
        if (!newResource.status) {
            newResource.status = "active";
            if (resourceType === "Composition") {
                newResource.status = "final";
            }
            if (resourceType === "List") {
                newResource.status = "current";
            }
        }
        if (resourceType === "List" && !newResource.mode) {
            newResource.mode = "snapshot";
        }
    }
    return newResource;
};

const DisplayFromFHIRReference = ({ reference, containedResourceJson }) => {

    const [expandContainedResourcesState, setExpandContainedResourcesState] = useState(false);
    if (!reference) {
        return <></>
    }
    if (Array.isArray(reference) || typeof reference !== "object") {
        return <>[ERROR: Reference data not interpretable.]</>;
    }
    let referenceRef = reference.reference;
    if (referenceRef && typeof referenceRef !== "string") {
        return <>[ERROR: Reference.reference data not interpretable.]</>;
    }
    let display = reference.display || referenceRef;
    if (display && typeof display !== "string") {
        return <>[ERROR: Reference.display data not interpretable.]</>;
    }
    let targetRef;
    if (referenceRef?.split("/").length === 2) {
        if (referenceRef.split("/")[1] && !isNaN(referenceRef.split("/")[1])) {
            targetRef = "foi-" + referenceRef.split("/")[1];
        }
        referenceRef = "/resources/" + referenceRef;
    } else if (reference.identifier && reference.identifier.system === "https://fevir.net/FLI" && reference.identifier.value) {
        targetRef = "fli-" + reference.identifier.value;
        referenceRef = "/FLI/" + reference.identifier.value;
        if (!reference.display) {
            display = reference.identifier.value;
        }
    }
    if (targetRef) {
        return <span>
            <NewTabLink href={referenceRef} target={targetRef} >{display}</NewTabLink>
        </span>
    }
    if (referenceRef && !referenceRef.startsWith("#")) {
        return <span>
            <a href={referenceRef} target='_blank' rel='noopener noreferrer'>{display}</a>
        </span>
    }
    if (containedResourceJson && referenceRef && referenceRef.startsWith("#")) {
        return <div>
            <span>{display} </span><span className={"unselectable"}
                style={{ cursor: "pointer", color: "blue" }}
                onClick={() => { setExpandContainedResourcesState(!expandContainedResourcesState); }}
            >{expandContainedResourcesState ? " ▼ collapse resource detail" : " ► expand resource detail"}
            </span>
            {expandContainedResourcesState === true &&
                <div style={{ marginLeft: "24px" }}>
                    <ContainedResourceDisplay resource={containedResourceJson} />
                </div>}
        </div>
    }
    return <span>{display}</span>
}

const displayStringFromFHIRReference = (reference) => {
    if (reference === undefined || reference === null || reference === '') {
        return ""
    }
    if (typeof reference === "object") {
        let referenceDisplay = reference.display || reference.reference || displayStringFromFHIRIdentifier(reference.identifier) || "";
        if (typeof referenceDisplay === "string" && referenceDisplay !== "") {
            return referenceDisplay;
        }
        if (reference.type) {
            return "[No data for " + reference.type + " Resource.]";
        }
    }
    if (typeof reference === "string") {
        return `${reference} [ERROR: Reference data not in expected Reference datatype.]`
    }
    return "[ERROR: Reference data not interpretable.]";
}

const getReferencedResource = async (foi, apiToken, setReferencedResourceJsonState) => {
    let referencedResourceJson = await getFhirJson(foi, null, apiToken, null);
    if (referencedResourceJson) {
        setReferencedResourceJsonState(referencedResourceJson);
    }
};

const ReferenceEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
    enableCreation, referencedResourceTypes, startCollapsed, doNotShowFieldLabel,
    startingResourceType, setProfile, selectProfile, selectAnyProfile, addElementValues,
    statisticType, statisticUnit, numberAnalyzed,
    setReferencedEvidenceRevisedState, setTriggerReloadState, setReferencedResourceState,
    shortTitle, containedResourceDictionary = {} }) => {
    console.log(containedResourceDictionary)
    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const combinedEvidenceMap = useSelector(selectComparativeEvidenceMap);
    const resourceCollection = useSelector(state => selectAllResources(state));
    const resourceCollectionStatus = useSelector(selectResourceCollectionStatus);

    if (shortTitle) {
        if (addElementValues) {
            addElementValues["shortTitle"] = shortTitle;
        } else {
            addElementValues = { "shortTitle": shortTitle };
        }
    }

    const [referenceState, setReferenceState] = useState({});
    const [startingReferenceState, setStartingReferenceState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed)
    const [selectResourceFromSearchState, setSelectResourceFromSearchState] = useState({});
    const [newResourceFoiState, setNewResourceFoiState] = useState("");
    const [referencedResourceJsonState, setReferencedResourceJsonState] = useState("");
    const [profileSelectionState, setProfileSelectionState] = useState({});
    const [editResourceContentState, setEditResourceContentState] = useState(false);
    const [referencedFoiState, setReferencedFoiState] = useState(null);
    const [newResourceState, setNewResourceState] = useState({});
    const [expandCreateNewResourceState, setExpandCreateNewResourceState] = useState(false);
    const [expandFhirDataEntryState, setExpandFhirDataEntryState] = useState(false);

    useEffect(() => {
        let startingProfileSelectionState = { "profileChoices": [] };
        if (startingResourceType && (setProfile || selectAnyProfile || selectProfile)) {
            const profileDictionary = ProfilesByResourceType[startingResourceType];
            const profileNames = Object.keys(profileDictionary);
            if (profileDictionary) {
                if (setProfile) {
                    if (profileNames.includes(setProfile)) {
                        let meta;
                        let section;
                        let content;
                        let profileUrl = "";
                        let citedArtifactClassificationToAdd;
                        let typeValue;
                        let categoryToAdd;
                        let useContextToAdd;
                        let membershipValue;
                        let actualValue;
                        let handlingValue;
                        let definitionValue;
                        let synthesisTypeValue;
                        let combinationMethodValue;
                        let characteristic;
                        let codeValue;
                        let additionalProfiles;
                        if (profileDictionary[setProfile]) {
                            profileUrl = profileDictionary[setProfile].url;
                            if (profileDictionary[setProfile].citedArtifactClassificationToAdd) {
                                citedArtifactClassificationToAdd = profileDictionary[setProfile].citedArtifactClassificationToAdd;
                            }
                            if (profileDictionary[setProfile].typeValue) {
                                typeValue = profileDictionary[setProfile].typeValue;
                            }
                            if (profileDictionary[setProfile].categoryToAdd) {
                                categoryToAdd = profileDictionary[setProfile].categoryToAdd;
                            }
                            if (profileDictionary[setProfile].useContextToAdd) {
                                useContextToAdd = profileDictionary[setProfile].useContextToAdd;
                            }
                            if (profileDictionary[setProfile].membershipValue) {
                                membershipValue = profileDictionary[setProfile].membershipValue;
                            }
                            if (profileDictionary[setProfile].definitionValue) {
                                definitionValue = profileDictionary[setProfile].definitionValue;
                            }
                            if (profileDictionary[setProfile].actualValue === true || profileDictionary[setProfile].actualValue === false) {
                                actualValue = profileDictionary[setProfile].actualValue;
                            }
                            if (profileDictionary[setProfile].handlingValue) {
                                handlingValue = profileDictionary[setProfile].handlingValue;
                            }
                            if (profileDictionary[setProfile].synthesisTypeValue) {
                                synthesisTypeValue = profileDictionary[setProfile].synthesisTypeValue;
                            }
                            if (profileDictionary[setProfile].combinationMethodValue) {
                                combinationMethodValue = profileDictionary[setProfile].combinationMethodValue;
                            }
                            if (profileDictionary[setProfile].codeValue) {
                                codeValue = profileDictionary[setProfile].codeValue;
                            }
                            if (profileDictionary[setProfile].sectionStarter) {
                                section = profileDictionary[setProfile].sectionStarter;
                            }
                            if (profileDictionary[setProfile].contentStarter) {
                                content = profileDictionary[setProfile].contentStarter;
                            }
                            if (profileDictionary[setProfile].characteristicStarter) {
                                characteristic = profileDictionary[setProfile].characteristicStarter;
                            }
                            if (profileDictionary[setProfile].additionalProfiles) {
                                additionalProfiles = profileDictionary[setProfile].additionalProfiles;
                            }
                        }
                        if (profileUrl) {
                            meta = { "profile": [profileUrl] };
                            if (additionalProfiles) {
                                meta.profile = meta.profile.concat(additionalProfiles);
                            }
                            startingProfileSelectionState = {
                                "profileChoices": [setProfile], "profileDictionary": profileDictionary,
                                'meta': meta,
                                'citedArtifactClassificationToAdd': citedArtifactClassificationToAdd,
                                'section': section,
                                'content': content,
                                'characteristic': characteristic,
                                'typeValue': typeValue,
                                'categoryToAdd': categoryToAdd,
                                'useContextToAdd': useContextToAdd,
                                'membershipValue': membershipValue,
                                'definitionValue': definitionValue,
                                'actualValue': actualValue,
                                'handlingValue': handlingValue,
                                'synthesisTypeValue': synthesisTypeValue,
                                'combinationMethodValue': combinationMethodValue,
                                'codeValue': codeValue,
                                "profileSelected": setProfile
                            };
                        }
                    }
                } else {
                    let profileChoices = [];
                    if (selectAnyProfile) {
                        profileChoices = profileNames;
                    }
                    if (selectProfile) {
                        for (const item of selectProfile) {
                            if (profileNames.includes(item)) {
                                profileChoices.push(item);
                            }
                        }
                    }
                    if (profileChoices.length === 0) {
                        startingProfileSelectionState = { "profileSelected": "none" };
                    } else if (profileChoices.length === 1) {
                        startingProfileSelectionState = {
                            "profileChoices": profileChoices, "profileDictionary": profileDictionary,
                            "profileSelected": profileChoices[0]
                        };
                    } else if (profileChoices.length > 1) {
                        startingProfileSelectionState = { "profileChoices": profileChoices, "profileDictionary": profileDictionary };
                    }
                }
            }
        }
        setProfileSelectionState(startingProfileSelectionState);
    }, []);

    useEffect(() => {
        //console.log("ReferenceEntry useEffect due to change in startingValue")
        let startingReference = { "reference": "", "type": "", "identifier": "", "display": "" };
        if (startingValue) {
            if (startingValue.extension) { startingReference.extension = startingValue.extension; }
            if (startingValue.reference) { startingReference.reference = startingValue.reference; }
            if (startingValue.type) { startingReference.type = startingValue.type; }
            if (startingValue.identifier) { startingReference.identifier = startingValue.identifier; }
            if (startingValue.display) { startingReference.display = startingValue.display; }
            if (!startingReference.display && addElementValues) {
                startingReference.display = addElementValues.title || addElementValues.name || shortTitle || "";
            }
        }
        setReferenceState(JSON.parse(JSON.stringify(startingReference)));
        setStartingReferenceState(JSON.parse(JSON.stringify(startingReference)));
    }, [startingValue]);

    useEffect(() => {
        if (newResourceFoiState) {
            setReferencedFoiState(newResourceFoiState);
        }
    }, [newResourceFoiState]);

    useEffect(() => {
        let resourceType = startingResourceType || referenceState.type;
        if (resourceType && profileSelectionState.profileSelected) {
            addProfileValuesToNewResource(resourceType, profileSelectionState.profileSelected, setNewResourceState, combinedEvidenceMap);
        }
    }, [profileSelectionState.profileSelected]);

    useEffect(() => {
        if (referencedFoiState && resourceCollectionStatus === "succeeded") {
            let referencedResourceJson;
            for (let entry of resourceCollection) {
                if (entry.id === referencedFoiState) {
                    referencedResourceJson = entry.revisedResourceJson;
                    break;
                }
            }
            if (referencedResourceJson) {
                setReferencedResourceJsonState(referencedResourceJson);
            } else {
                getReferencedResource(referencedFoiState, apiToken, setReferencedResourceJsonState);
            }
        }
    }, [referencedFoiState]);

    useEffect((() => {
        //console.log("ReferenceEntry useEffect due to change in referenceState.type")
        if (startingResourceType) {
            if (!referenceState.type) {
                setReferenceState(prevState => { return { ...prevState, 'type': startingResourceType } });
            }
        }
    }), [startingResourceType, referenceState.type]);

    useEffect((() => {
        //console.log("ReferenceEntry useEffect due to change in referenceState.reference")
        if (referenceState.reference?.split("/").length === 2) {
            let presumedFoi = referenceState.reference.split("/")[1];
            if (!isNaN(presumedFoi)) {
                setReferencedFoiState(presumedFoi);
            }
        }
        /*
        //TO DO, do we do something else or do a FOI lookup?
        else if (referenceState.reference?.identifier && referenceState.reference.identifier.system === "https://fevir.net/FLI" && referenceState.reference.identifier.value) {
            referenceRef = "/FLI/" + referenceState.reference.identifier.value;
            if (!referenceState.reference.display) {
                display = referenceState.reference.identifier.value;
            }
        }
        */
        if (setTriggerReloadState) {
            setTriggerReloadState(true);
        }
    }), [referenceState.reference]);

    useEffect((() => {
        if (referenceState.type) {
            let profileChoices;
            if (selectAnyProfile || selectProfile) {
                const profileDictionary = ProfilesByResourceType[referenceState.type];
                if (profileDictionary) {
                    profileChoices = [];
                    const profileNames = Object.keys(profileDictionary);
                    if (selectAnyProfile) {
                        profileChoices = profileNames;
                    }
                    if (selectProfile) {
                        for (const item of selectProfile) {
                            if (profileNames.includes(item)) {
                                profileChoices.push(item);
                            }
                        }
                    }
                    if (profileChoices.length === 0) {
                        setProfileSelectionState({ "profileChoices": [], "profileSelected": "none" });
                    } else if (profileChoices.length === 1) {
                        setProfileSelectionState({
                            "profileChoices": profileChoices, "profileDictionary": profileDictionary,
                            "profileSelected": profileChoices[0]
                        });
                    } else if (profileChoices.length > 1) {
                        setProfileSelectionState({ "profileChoices": profileChoices, "profileDictionary": profileDictionary });
                    }
                } else {
                    setProfileSelectionState({ "profileChoices": [], "profileSelected": "none" });
                }
            }
        }
    }), [referenceState.type, selectAnyProfile, selectProfile]);

    useEffect((() => {
        if (selectResourceFromSearchState.selectedResourceReference) {
            setReferenceState(selectResourceFromSearchState.selectedResourceReference);
        }
    }), [selectResourceFromSearchState.selectedResourceReference])

    useEffect((() => {
        //console.log("ReferenceEntry useEffect due to change in referenceState")
        if (Object.keys(referenceState).length &&
            !jsonEquals(startingReferenceState, referenceState) &&
            !(!startingReferenceState.display && !startingReferenceState.reference &&
                !startingReferenceState.identifier && !startingReferenceState.extension &&
                !referenceState.display && !referenceState.reference && !referenceState.identifier &&
                !referenceState.extension
            )
        ) {
            let newReference = {};
            if (referenceState.extension) { newReference.extension = referenceState.extension; }
            if (referenceState.reference) { newReference.reference = referenceState.reference; }
            if (referenceState.type) { newReference.type = referenceState.type; }
            if (referenceState.identifier && Object.keys(referenceState.identifier).length) {
                newReference.identifier = referenceState.identifier;
            }
            if (referenceState.display) {
                newReference.display = referenceState.display;
                setNewResourceState(prevState => { return { ...prevState, "title": referenceState.display }; });
            } else if (addElementValues) {
                newReference.display = addElementValues.title || addElementValues.name || shortTitle || "";
            }
            if (Object.keys(newReference).length === 0 || (!newReference.display && !newReference.reference && !newReference.identifier && !newReference.extension)) {
                newReference = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newReference } });
        }
    }), [referenceState]);

    if (startCollapsedState) {
        return <div>
            {!doNotShowFieldLabel && <b>{fieldLabel}: </b>}
            {startingValue && <DisplayFromFHIRReference reference={startingValue}
                containedResourceJson={startingValue.reference && containedResourceDictionary[startingValue.reference]} />}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
            &nbsp;&nbsp;
            {(enableCreation && (referenceState.reference || referenceState.identifier) &&
                referencedResourceJsonState) &&
                <>
                    <Button className="formButton" style={{ color: "#FFFFFF" }}
                        positive
                        content={`Edit the Resource Content`} disabled={editResourceContentState}
                        onClick={() => {
                            setStartCollapsedState(false);
                            setEditResourceContentState(true);
                        }} />
                    <br /></>
            }
        </div>
    } else if (Object.keys(referenceState).length > 0) {
        return <>
            {(fieldLabel && !doNotShowFieldLabel) && <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>}
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='display'
                    fieldLabel={'Display of ' + fieldLabel}
                    startingValue={referenceState.display || addElementValues?.title || shortTitle}
                    setResourceState={setReferenceState} />
                <Button className="formButton" style={{ color: "#000000" }}
                    content="Search Resources to Select One"
                    onClick={() => { setSelectResourceFromSearchState({ "modalOpen": true }); }} />
                <br />
                {(editResourceContentState && referencedResourceJsonState.id) &&
                    <ReferencedResourceContentEntry
                        referencedResourceJsonState={referencedResourceJsonState}
                        statisticType={statisticType} statisticUnit={statisticUnit}
                        setReferencedResourceState={setReferencedResourceState}
                        numberAnalyzed={numberAnalyzed}
                        setTriggerReloadState={setTriggerReloadState}
                        setReferencedEvidenceRevisedState={setReferencedEvidenceRevisedState}
                    />}
                {enableCreation &&
                    <>
                        {expandCreateNewResourceState === true ?
                            <>
                                <StringEntry elementName='display' fieldLabel={'Title of ' + fieldLabel + ' Resource'}
                                    startingValue={referenceState.display || addElementValues?.title || shortTitle}
                                    setResourceState={setReferenceState} />
                                {(profileSelectionState.showProfileSelector &&
                                    profileSelectionState.profileSelected !== "none") &&
                                    <SelectProfileForNewResource profileSelectionState={profileSelectionState}
                                        setProfileSelectionState={setProfileSelectionState} />}
                                {referencedResourceTypes?.length ?
                                    <CodeEntry elementName='type' fieldLabel='Resource Type'
                                        startingValue={referenceState.type} setResourceState={setReferenceState}
                                        allowedValues={referencedResourceTypes} />
                                    :
                                    <CodeEntry elementName='type' fieldLabel='Resource Type'
                                        startingValue={referenceState.type}
                                        allowedValues={allFhirResourceTypes} dataEntryStyle="dropdownsearch"
                                        setResourceState={setReferenceState} />
                                }
                                {(referenceState.reference || referenceState.identifier) ?
                                    <>
                                        {referencedResourceJsonState &&
                                            <>
                                                <Button className="formButton" style={{ color: "#FFFFFF" }}
                                                    positive={true}
                                                    content={`Edit Resource Content`} disabled={editResourceContentState}
                                                    onClick={() => {
                                                        setEditResourceContentState(true);
                                                    }} />
                                                &nbsp;&nbsp;&nbsp;
                                                {referencedResourceJsonState.id && <span>
                                                    <a href={referencedResourceJsonState.resourceType ? '/resources/' + referencedResourceJsonState.resourceType + "/" + referencedResourceJsonState.id + "/edit" : '/' + referencedResourceJsonState.id}
                                                        target='_blank'
                                                        rel='noopener noreferrer' >Edit this Resource in new window</a>
                                                </span>}
                                                <br />
                                            </>}
                                        {((profileSelectionState.profileChoices?.length || selectAnyProfile || selectProfile) &&
                                            !profileSelectionState.profileSelected && !setProfile &&
                                            profileSelectionState.profileSelected !== "none") ?
                                            <Button className="formButton" style={{ color: "#000000" }}
                                                content={`Select Profile to replace with new ${startingResourceType || referenceState.type} Resource`}
                                                onClick={() => {
                                                    setProfileSelectionState(prevState => {
                                                        return {
                                                            ...prevState,
                                                            showProfileSelector: true,
                                                            profileSelected: ""
                                                        };
                                                    });
                                                }} />
                                            :
                                            <Button className="formButton" style={{ color: "#000000" }}
                                                content={`Replace with new ${startingResourceType || referenceState.type || "[MUST SELECT A TYPE FIRST]"} Resource`}
                                                onClick={async () => {
                                                    let newResource = createNewReferencedResource(startingResourceType ||
                                                        referenceState.type, newResourceState, addElementValues);
                                                    let title = referenceState.display;
                                                    let resourceFOI;
                                                    let response = await submitFhirJson(newResource, title, apiToken);
                                                    if (response?.success) {
                                                        resourceFOI = response.formstateid.toString();
                                                        newResource.id = resourceFOI;
                                                        let resourceBody = {
                                                            id: resourceFOI,
                                                            displayTitle: title,
                                                            resourceType: newResource.resourceType,
                                                            resourceJson: newResource,
                                                            revisedResourceJson: newResource,
                                                            usageData: {
                                                                totalviews: 0,
                                                                totaledits: 1,
                                                                totalusersviewed: 0,
                                                                totalusersedited: 1
                                                            },
                                                            permissionData: {
                                                                editpermission: true,
                                                                readpermission: true,
                                                                adminpermission: true,
                                                                status: "active"
                                                            },
                                                            editors: [{ userid: userId, name: userName }]
                                                        }
                                                        dispatch(resourceAdded(resourceBody));
                                                        setNewResourceFoiState(resourceFOI);
                                                        setReferenceState(prevState => {
                                                            let reference = `${startingResourceType || referenceState.type}/${resourceFOI}`;
                                                            return {
                                                                ...prevState,
                                                                reference: reference,
                                                                type: startingResourceType || referenceState.type
                                                            };
                                                        });
                                                    } else {
                                                        alert("It did not work.");
                                                    }
                                                }} />
                                        }
                                        &nbsp;&nbsp;&nbsp;
                                        {(newResourceFoiState && !referencedResourceJsonState) && <span>
                                            <a href={'https://fevir.net/' + newResourceFoiState}
                                                target='_blank' rel='noopener noreferrer' >Edit this new Resource on FEvIR Platform</a>
                                        </span>}
                                        <br />
                                    </>
                                    :
                                    <>
                                        {((startingResourceType || referenceState.type) &&
                                            (profileSelectionState.profileChoices?.length > 0 || selectAnyProfile || selectProfile) &&
                                            !profileSelectionState.profileSelected &&
                                            !setProfile &&
                                            profileSelectionState.profileSelected !== "none")
                                            ?
                                            <Button className="formButton" style={{ color: "#000000" }}
                                                content={`Select Profile to create this new ${startingResourceType ||
                                                    referenceState.type} Resource`}
                                                onClick={() => {
                                                    setProfileSelectionState(prevState => {
                                                        return {
                                                            ...prevState,
                                                            showProfileSelector: true,
                                                            profileSelected: ""
                                                        };
                                                    });
                                                }} />
                                            :
                                            <Button className="formButton" style={{ color: "#FFFFFF" }}
                                                positive={true}
                                                content={`Create this ${setProfile ||
                                                    profileSelectionState?.profileSelected || startingResourceType ||
                                                    referenceState.type || "[MUST SELECT A TYPE FIRST]"} Resource`}
                                                onClick={async () => {
                                                    let newResource = createNewReferencedResource(startingResourceType ||
                                                        referenceState.type, newResourceState, addElementValues);

                                                    let title = referenceState.display;
                                                    let resourceFOI;
                                                    let response = await submitFhirJson(newResource, title, apiToken);
                                                    if (response?.success) {
                                                        resourceFOI = response.formstateid.toString();
                                                        newResource.id = resourceFOI;
                                                        let resourceBody = {
                                                            id: resourceFOI,
                                                            displayTitle: title,
                                                            resourceType: newResource.resourceType,
                                                            resourceJson: newResource,
                                                            revisedResourceJson: newResource,
                                                            usageData: {
                                                                totalviews: 0,
                                                                totaledits: 1,
                                                                totalusersviewed: 0,
                                                                totalusersedited: 1
                                                            },
                                                            permissionData: {
                                                                editpermission: true,
                                                                readpermission: true,
                                                                adminpermission: true,
                                                                status: "active"
                                                            },
                                                            editors: [{ userid: userId, name: userName }]
                                                        }
                                                        dispatch(resourceAdded(resourceBody));
                                                        setNewResourceFoiState(resourceFOI);
                                                        setReferenceState(prevState => {
                                                            let reference = `${startingResourceType || referenceState.type}/${resourceFOI}`;
                                                            return {
                                                                ...prevState,
                                                                reference: reference,
                                                                type: startingResourceType || referenceState.type
                                                            };
                                                        });
                                                    } else {
                                                        alert("It did not work.");
                                                    }
                                                }} />
                                        }
                                        <br />
                                    </>}
                            </>
                            :
                            <>
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Create New Resource"
                                    onClick={() => { setExpandCreateNewResourceState(true); }} />
                                <br />
                            </>
                        }
                    </>}
                {expandFhirDataEntryState === true ?
                    <>
                        <StringEntry elementName='display'
                            fieldLabel={'Display of ' + fieldLabel}
                            startingValue={referenceState.display || addElementValues?.title || shortTitle}
                            setResourceState={setReferenceState} />
                        <StringEntry elementName='reference'
                            fieldLabel='Referenced resource absolute or relative URI'
                            startingValue={referenceState.reference} setResourceState={setReferenceState} />
                        {referencedResourceTypes ?
                            <CodeEntry elementName='type' fieldLabel='Resource Type'
                                startingValue={referenceState.type}
                                setResourceState={setReferenceState}
                                allowedValues={referencedResourceTypes} />
                            :
                            <StringEntry elementName='type' fieldLabel='Resource Type'
                                startingValue={referenceState.type}
                                setResourceState={setReferenceState} />
                        }
                        <IdentifierEntry elementName='identifier'
                            fieldLabel='Identifier' containedResourceDictionary={containedResourceDictionary}
                            startingValue={referenceState.identifier} startCollapsed={true}
                            setResourceState={setReferenceState}
                        />
                        <br />
                    </>
                    :
                    <p>
                        <span style={{ cursor: "pointer" }} onClick={() => { setExpandFhirDataEntryState(true); }}>Click to edit FHIR elements.</span>
                    </p>
                }
            </div>
            {selectResourceFromSearchState.modalOpen &&
                <SelectResourceFromSearchModal selectedResourceType={referenceState.type || startingResourceType}
                    allowedResourceTypes={referencedResourceTypes}
                    selectedProfile={setProfile} allowedProfiles={selectProfile}
                    setSelectResourceFromSearchState={setSelectResourceFromSearchState}
                    setStartCollapsedState={setStartCollapsedState} />}
        </>
    }
}

export default ReferenceEntry;
export {
    createNewReferencedResource, DisplayFromFHIRReference, displayStringFromFHIRReference,
    SelectResourceFromSearchModal, SelectProfileForNewResource
};